<template>
  <div ref="scroller" class="pdfz" @wheel="onWheel">
    <div v-if="loading" class="pdfz__state">
      <i class="mdi mdi-loading mdi-spin"></i>
      <span>{{ $t("course.loadingPdf") }}</span>
    </div>

    <!-- `width: max-content` é o que faz a barra lateral aparecer: com zoom
         acima da largura do palco as páginas ficam mais largas que o scroller,
         como acontece no Ctrl+scroll. Abaixo disso elas ficam centralizadas. -->
    <div class="pdfz__pages">
      <div
        v-for="p in pages"
        :key="p.num"
        class="pdfz__page"
        :data-num="p.num"
        :style="{
          width: `${Math.floor(p.width * scale)}px`,
          height: `${Math.floor(p.height * scale)}px`,
        }"
      ></div>
    </div>
  </div>
</template>

<script>
// Card 86aknb8n7: o zoom pelo `#zoom=` do visualizador nativo não abria barra
// lateral (o Chrome reaplica o ajuste à largura), recarregava o PDF a cada
// clique e não funcionava no Safari. Aqui o PDF é desenhado com pdf.js, então o
// zoom é só redesenhar as páginas num tamanho maior dentro de um scroller.

// pdf.js pesa algumas centenas de KB: só é baixado quando um PDF é aberto.
let pdfjsPromise = null;
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import(
      /* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf"
    ).then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/legacy/build/pdf.worker.min.js",
        import.meta.url
      ).toString();
      return lib;
    });
    pdfjsPromise.catch(() => {
      pdfjsPromise = null;
    });
  }
  return pdfjsPromise;
}

// Margem interna do scroller; entra na conta do "100% = largura do palco".
const GUTTER = 16;
// Teto de pixels por canvas: o Safari do iPhone recusa canvas acima de ~16 MP.
// Em zoom alto a nitidez extra (devicePixelRatio) é o que cede primeiro.
const MAX_CANVAS_PIXELS = 12 * 1024 * 1024;
// Quanto de rolagem do trackpad/roda vale um degrau de zoom.
const WHEEL_STEP = 60;
// Destinos com o endereço fora do http(s)/mailto não viram link clicável.
const SAFE_LINK = /^(https?:|mailto:)/i;

export default {
  name: "PdfZoomViewer",
  props: {
    url: { type: String, required: true },
    // Percentual; 100 = página na largura do palco.
    zoom: { type: Number, default: 100 },
  },
  data() {
    return {
      loading: true,
      pages: [],
      fitScale: 1,
    };
  },
  computed: {
    scale() {
      return (this.fitScale * this.zoom) / 100;
    },
  },
  watch: {
    url() {
      this.open();
    },
    scale(next, prev) {
      this.keepViewPosition(prev, next);
      this.renderVisible();
    },
  },
  created() {
    // Fora do data(): objetos do pdf.js são enormes e o Vue 2 tentaria
    // torná-los reativos.
    this.doc = null;
    this.pdfjs = null;
    this.annotations = {};
    this.linkService = this.createLinkService();
    this.laidOut = false;
    this.lastZoom = this.zoom;
    this.loadingTask = null;
    this.pdfPages = {};
    this.rendered = {};
    this.tasks = {};
    this.visible = new Set();
    this.observer = null;
    this.resizeObserver = null;
    this.wheelAcc = 0;
    this.maxPageWidth = 0;
  },
  mounted() {
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.updateFitScale());
      this.resizeObserver.observe(this.$refs.scroller);
    } else {
      window.addEventListener("resize", this.updateFitScale);
    }
    this.open();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener("resize", this.updateFitScale);
    this.close();
  },
  methods: {
    async open() {
      this.close();
      this.loading = true;
      this.pages = [];
      const url = this.url;
      try {
        const pdfjs = await loadPdfjs();
        if (url !== this.url) return;
        this.pdfjs = pdfjs;
        this.loadingTask = pdfjs.getDocument({ url });
        const doc = await this.loadingTask.promise;
        if (url !== this.url) {
          doc.destroy();
          return;
        }
        this.doc = doc;

        const pages = await Promise.all(
          Array.from({ length: doc.numPages }, (_, i) => doc.getPage(i + 1))
        );
        const sizes = pages.map((page) => {
          this.pdfPages[page.pageNumber] = page;
          const vp = page.getViewport({ scale: 1 });
          return { num: page.pageNumber, width: vp.width, height: vp.height };
        });
        this.maxPageWidth = Math.max(...sizes.map((s) => s.width));
        this.updateFitScale();
        this.pages = sizes;
        this.loading = false;
        this.$nextTick(() => {
          this.laidOut = true;
          this.observePages();
        });
      } catch (err) {
        if (url !== this.url) return;
        // Link expirado, bucket sem CORS, arquivo corrompido: o pai volta
        // para o visualizador nativo do navegador.
        this.loading = false;
        this.$emit("error", err);
      }
    },
    close() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      Object.values(this.tasks).forEach((t) => t.cancel());
      this.tasks = {};
      this.rendered = {};
      this.visible = new Set();
      this.pdfPages = {};
      this.annotations = {};
      if (this.loadingTask) this.loadingTask.destroy();
      this.loadingTask = null;
      this.doc = null;
      this.laidOut = false;
    },
    updateFitScale() {
      const el = this.$refs.scroller;
      if (!el || !this.maxPageWidth) return;
      const available = el.clientWidth - GUTTER * 2;
      if (available <= 0) return;
      this.fitScale = available / this.maxPageWidth;
    },
    observePages() {
      const root = this.$refs.scroller;
      if (!root) return;
      const els = root.querySelectorAll(".pdfz__page");
      if (typeof IntersectionObserver === "undefined") {
        els.forEach((el) => this.visible.add(Number(el.dataset.num)));
        this.renderVisible();
        return;
      }
      // Só as páginas perto da área visível são desenhadas; um PDF de 60
      // páginas em 300% não cabe na memória de um celular.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const num = Number(entry.target.dataset.num);
            if (entry.isIntersecting) {
              this.visible.add(num);
              this.renderPage(num);
            } else {
              this.visible.delete(num);
            }
          });
        },
        { root, rootMargin: "800px 400px" }
      );
      els.forEach((el) => this.observer.observe(el));
    },
    renderVisible() {
      this.visible.forEach((num) => this.renderPage(num));
    },
    async renderPage(num) {
      const page = this.pdfPages[num];
      const scale = this.scale;
      if (!page || this.rendered[num] === scale) return;
      if (this.tasks[num]) this.tasks[num].cancel();
      this.rendered[num] = scale;

      const viewport = page.getViewport({ scale });
      const cssW = Math.floor(viewport.width);
      const cssH = Math.floor(viewport.height);
      const dpr = window.devicePixelRatio || 1;
      const ratio = Math.min(
        dpr,
        Math.sqrt(MAX_CANVAS_PIXELS / Math.max(1, cssW * cssH))
      );

      // Desenha num canvas novo e só troca no fim: redimensionar o canvas
      // visível apagaria a página e ela piscaria em branco a cada zoom.
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      const task = page.render({
        canvasContext: canvas.getContext("2d"),
        viewport: page.getViewport({ scale: scale * ratio }),
      });
      this.tasks[num] = task;
      try {
        await task.promise;
      } catch (err) {
        // Cancelado por um zoom novo ou pela troca de material.
        if (this.rendered[num] === scale) delete this.rendered[num];
        return;
      } finally {
        if (this.tasks[num] === task) delete this.tasks[num];
      }
      const holder =
        this.$refs.scroller &&
        this.$refs.scroller.querySelector(`.pdfz__page[data-num="${num}"]`);
      if (!holder || this.rendered[num] !== scale) return;
      holder.textContent = "";
      holder.appendChild(canvas);
      await this.renderLinks(num, page, viewport, holder);
    },
    // O canvas é só imagem: sem esta camada os links do PDF (sites, sumário)
    // não são clicáveis. É a mesma camada de anotações do leitor do pdf.js,
    // posicionada por cima da página na escala atual.
    async renderLinks(num, page, viewport, holder) {
      if (!this.pdfjs || !this.pdfjs.AnnotationLayer) return;
      if (!this.annotations[num]) {
        try {
          this.annotations[num] = await page.getAnnotations({
            intent: "display",
          });
        } catch (err) {
          this.annotations[num] = [];
        }
      }
      const annotations = this.annotations[num];
      // Zoom ou troca de material enquanto as anotações carregavam.
      if (!annotations.length || this.rendered[num] !== viewport.scale) return;
      if (!holder.isConnected) return;

      const layer = document.createElement("div");
      layer.className = "annotationLayer";
      holder.appendChild(layer);
      try {
        this.pdfjs.AnnotationLayer.render({
          viewport: viewport.clone({ dontFlip: true }),
          div: layer,
          annotations,
          page,
          linkService: this.linkService,
          downloadManager: null,
          renderForms: false,
          enableScripting: false,
        });
      } catch (err) {
        // Anotação que o pdf.js não sabe desenhar: fica só a imagem da
        // página, como antes.
        layer.remove();
      }
    },
    // O que o pdf.js chama ao montar e ao clicar num link. Link externo abre
    // em outra aba, para o aluno não sair da aula; link interno (sumário,
    // "ver página 5") rola o próprio visualizador até o destino.
    createLinkService() {
      return {
        addLinkAttributes: (link, url) => {
          if (!url || !SAFE_LINK.test(url)) return;
          link.href = url;
          link.title = url;
          link.target = "_blank";
          link.rel = "noopener noreferrer nofollow";
        },
        getDestinationHash: () => "#",
        getAnchorUrl: () => "#",
        goToDestination: (dest) => this.goToDestination(dest),
        executeNamedAction: () => {},
        cachePageRef: () => {},
        isPageVisible: () => true,
        isPageCached: () => true,
      };
    },
    async goToDestination(dest) {
      const doc = this.doc;
      const el = this.$refs.scroller;
      if (!doc || !el) return;
      try {
        const explicit =
          typeof dest === "string" ? await doc.getDestination(dest) : dest;
        if (!Array.isArray(explicit)) return;
        const [ref, mode, , top] = explicit;
        const pageIndex =
          typeof ref === "object" && ref !== null
            ? await doc.getPageIndex(ref)
            : Number(ref);
        const num = pageIndex + 1;
        const holder = el.querySelector(`.pdfz__page[data-num="${num}"]`);
        if (!holder) return;
        let offset = 0;
        // XYZ traz a altura exata do destino dentro da página.
        const page = this.pdfPages[num];
        if (page && mode && mode.name === "XYZ" && typeof top === "number") {
          const [, y] = page
            .getViewport({ scale: this.scale })
            .convertToViewportPoint(0, top);
          offset = Math.max(0, y);
        }
        el.scrollTop = holder.offsetTop + offset - GUTTER;
      } catch (err) {
        // Destino quebrado no PDF: o clique não faz nada.
      }
    },
    // Zoom: mantém no centro da tela o mesmo ponto do documento, senão cada
    // clique joga o aluno para outro trecho da página. Mudança de tamanho do
    // palco (expandir, girar o celular): mantém o mesmo ponto no topo, porque
    // o centro antigo já não vale para a caixa nova.
    keepViewPosition(prevScale, nextScale) {
      const el = this.$refs.scroller;
      const byZoom = this.zoom !== this.lastZoom;
      this.lastZoom = this.zoom;
      // Na abertura o primeiro ajuste de escala não é zoom do aluno: o PDF
      // tem que começar no topo.
      if (!el || !this.laidOut || !prevScale || !nextScale) return;
      const factor = nextScale / prevScale;
      if (!byZoom) {
        const left = el.scrollLeft * factor;
        const top = el.scrollTop * factor;
        this.$nextTick(() => {
          el.scrollLeft = left;
          el.scrollTop = top;
        });
        return;
      }
      const cx = el.scrollLeft + el.clientWidth / 2;
      const cy = el.scrollTop + el.clientHeight / 2;
      this.$nextTick(() => {
        el.scrollLeft = cx * factor - el.clientWidth / 2;
        el.scrollTop = cy * factor - el.clientHeight / 2;
      });
    },
    // Ctrl+roda (e a pinça do trackpad, que chega como Ctrl+roda) aproxima o
    // PDF em vez da página inteira, como no leitor nativo.
    onWheel(e) {
      if (!e.ctrlKey) return;
      e.preventDefault();
      this.wheelAcc += e.deltaY;
      if (Math.abs(this.wheelAcc) < WHEEL_STEP) return;
      this.$emit("zoom-step", this.wheelAcc < 0 ? 1 : -1);
      this.wheelAcc = 0;
    },
  },
};
</script>

<style scoped>
.pdfz {
  position: absolute;
  inset: 0;
  overflow: auto;
  overscroll-behavior: contain;
}
.pdfz__pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  width: max-content;
  min-width: 100%;
  padding: 16px;
}
.pdfz__page {
  position: relative;
  flex: none;
  background: var(--dk-surface, #fff);
  box-shadow: 0 1px 4px rgba(15, 11, 20, 0.18);
}
.pdfz__page >>> canvas {
  display: block;
  width: 100%;
  height: 100%;
}
/* Camada de links do pdf.js (mesmas regras do pdf_viewer.css). Os elementos
   são criados pelo pdf.js, fora do escopo do componente: por isso o >>>. */
.pdfz__page >>> .annotationLayer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.pdfz__page >>> .annotationLayer section {
  position: absolute;
  text-align: initial;
}
.pdfz__page >>> .annotationLayer .linkAnnotation > a {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  font-size: 1em;
  cursor: pointer;
}
.pdfz__page >>> .annotationLayer .linkAnnotation > a:hover {
  background: rgba(255, 213, 0, 0.2);
}
.pdfz__state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #6b6478;
  font-size: 0.9rem;
}
</style>
